

import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../App';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import {
  PlusCircle,
  Calendar,
  Clock,
  UserCircle2,
  Building2,
  Euro,
  Edit3,
  Trash2,
  X
} from 'lucide-react';
import { toast } from 'sonner';


function formatDuration(minutesFloat) {
  if (minutesFloat == null || isNaN(minutesFloat)) return "—";

  const totalMinutes = Math.round(minutesFloat);
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;

  return `${h}h ${m}m`;
}


function fmt(dateStr) {
  if (!dateStr) return '—';

  if (typeof dateStr === 'object') {
    if ('$date' in dateStr) {
      dateStr = dateStr['$date'];
    } else if (dateStr.$date) {
      dateStr = dateStr.$date;
    }
  }

  const d = new Date(dateStr);

  if (Number.isNaN(d.getTime())) {
    return '—';
  }

  return d.toLocaleString('fr-FR');
}


export default function Prestations() {

  const { axiosInstance, user } = useAuth();


  /*
  ==========================================================
  GESTION DES DROITS ADMINISTRATEURS / CHEFS
  ==========================================================

  Un utilisateur est considéré comme chef si :

  - role === "chef"
  - role === "chief"
  - care_role === "chef"
  - care_role === "chief"
  - is_chef === true
  - is_chief === true

  Compatibilité ajoutée pour Tatiana Tabougnia :
  - id utilisateur
  - _id MongoDB

  Cela permet à Tatiana d'avoir immédiatement les droits chef
  même si son rôle actuel dans la base est encore "nurse".
  ==========================================================
  */

  const CHEF_USER_IDS = [
    '96eb59c8-8a0f-48fb-babb-62ea934fa604',
    '6a21b1ae22ae5c77adf02f9f'
  ];


  const normalizeRole = (value) =>
    String(value || '')
      .trim()
      .toLowerCase();


  const userRole = normalizeRole(user?.role);
  const userCareRole = normalizeRole(user?.care_role);


  const isAdmin = userRole === 'admin';


  const isChef =
    userRole === 'chef' ||
    userRole === 'chief' ||
    userCareRole === 'chef' ||
    userCareRole === 'chief' ||
    user?.is_chef === true ||
    user?.is_chief === true ||
    CHEF_USER_IDS.includes(String(user?.id || '')) ||
    CHEF_USER_IDS.includes(String(user?._id || ''));


  /*
  ==========================================================
  ACCÈS DE GESTION COMPLET
  ==========================================================

  Admin = accès complet
  Chef = accès complet

  Les infirmiers/aides-soignants ordinaires ne récupèrent PAS
  automatiquement ces permissions.
  */

  const canManage = isAdmin || isChef;


  const [prestations, setPrestations] = useState([]);

  const [loading, setLoading] = useState(false);

  const [creating, setCreating] = useState(false);

  const [creationMode, setCreationMode] = useState('normal');

  const [multipleMode, setMultipleMode] = useState(false);
  const today = new Date();
  const [multipleYear, setMultipleYear] = useState(String(today.getFullYear()));
  const [multipleMonth, setMultipleMonth] = useState(String(today.getMonth() + 1).padStart(2, '0'));
  const [multipleDates, setMultipleDates] = useState({});
  const [multipleStartTime, setMultipleStartTime] = useState('07:00');
  const [multipleEndTime, setMultipleEndTime] = useState('14:00');

  const [editingId, setEditingId] = useState(null);

  const [soignants, setSoignants] = useState([]);

  const [etablissements, setEtablissements] = useState([]);

  const [
    etablissementsComplets,
    setEtablissementsComplets
  ] = useState([]);

  const [typeSoignant, setTypeSoignant] = useState('');

  const [
    isTarificationPourcentage,
    setIsTarificationPourcentage
  ] = useState(false);


  const [newPrestation, setNewPrestation] = useState({
    name: '',
    description: '',
    assigned_to: '',
    etablissement_id: '',
    dateStart: '',
    dateEnd: '',
    pricing_type: 'normal',
    prestation_type: 'normal',
    base_rate: 0,
    travel_fee: 0,
    final_rate: 0,
    observation: ''
  });


  const [filterYear, setFilterYear] = useState('');

  const [filterMonth, setFilterMonth] = useState('');

  const [
    filterEtablissement,
    setFilterEtablissement
  ] = useState('');

  const [filterRole, setFilterRole] = useState('');

  const [filterUser, setFilterUser] = useState('');

  const [usersByRole, setUsersByRole] = useState({});
  const [initialLoading, setInitialLoading] = useState(true);

  const [currentPage, setCurrentPage] = useState(1);

  const ITEMS_PER_PAGE = 30;


  /*
  ==========================================================
  CHARGEMENT INITIAL
  ==========================================================
  */

  useEffect(() => {

    fetchPrestations();

    /*
    IMPORTANT :
    Avant, cette partie était réservée uniquement à :
        user.role === 'admin'

    Maintenant :
        admin + chef
    */

    if (canManage) {
      fetchSoignants('');
      fetchUsersByRole();
    }

    fetchEtablissements();

  }, []);


  /*
  ==========================================================
  CHARGEMENT DES PRESTATIONS
  ==========================================================
  */

  const fetchPrestations = async (showLoader = true) => {

    if (showLoader) {
      setInitialLoading(true);
    }

    try {

      const res = await axiosInstance.get('/prestations');

      const data = res.data

        .filter(p => p.status !== 'deleted')

        .map(p => ({

          _id: p._id,

          name:
            p.name ||
            p.type ||
            '—',

          description:
            p.description ||
            p.notes ||
            '—',

          type_soignant:
            p.type_soignant,

          dateStart:
            p.dateStart
              ? new Date(p.dateStart)
              : null,

          dateEnd:
            p.dateEnd
              ? new Date(p.dateEnd)
              : null,

          dureeMinutes:
            p.dureeMinutes || 0,

          // assigned_to:
          //   p.assigned_to ||
          //   p.soignant ||
          //   null,
          assigned_to:
            p.assigned_to?._id ||
            p.assigned_to?.id ||
            p.assigned_to ||
            p.soignant?._id ||
            p.soignant?.id ||
            p.soignant ||
            null,

          assigned_uuid:
            p.assigned_uuid ||
            null,

          assigned_mongo_id:
            p.assigned_mongo_id ||
            null,

          // assigned_name:
          //   p.assigned_name ||
          //   '—',
          assigned_name:
            p.assigned_name ||
            (
              typeof p.assigned_to === 'object'
                ? `${p.assigned_to?.first_name || ''} ${p.assigned_to?.last_name || ''}`.trim()
                : ''
            ) ||
            (
              typeof p.soignant === 'object'
                ? `${p.soignant?.first_name || ''} ${p.soignant?.last_name || ''}`.trim()
                : ''
            ) ||
            '—',

          etablissement_id:
            p.etablissement_id ||
            p.etablissement?.id ||
            p.institution_id ||
            null,

          etablissement_name:
            p.etablissement?.name ||
            p.institution_name ||
            p.etablissement_name ||
            '—',

          status:
            p.status ||
            p.statut ||
            'pending',

          started_at:
            p.started_at ||
            null,

          ended_at:
            p.ended_at ||
            null,

          actual_duration:
            p.actual_duration != null
              ? p.actual_duration
              : null,

          pricing_type:
            p.pricing_type,

          prestation_type:
            p.prestation_type ||
            'normal',

          base_rate:
            p.base_rate,

          travel_fee:
            p.travel_fee,

          final_rate:
            p.final_rate,

          gain_estime:
            p.gain_estime ?? 0,

          gain_final:
            Number(p.gain_final) || 0,

          observation:
            p.observation ||
            null
        }));


      setPrestations(data);

    } catch (err) {

      console.error(
        'Erreur chargement prestations :',
        err
      );

      toast.error(
        "Impossible de charger les prestations"
      );

    } finally {

      setLoading(false);
      if (showLoader) {
        setInitialLoading(false);
      }

    }
  };


  /*
  ==========================================================
  CHARGEMENT DES SOIGNANTS
  ==========================================================
  */

  const fetchSoignants = async (type) => {

    if (!type) {

      setSoignants([]);

      return;
    }


    try {

      const res =
        await axiosInstance.get('/users');


      const filtered = res.data

        .filter(
          u =>
            u.role === type &&
            u.status === 'approved'
        )

        .map(u => {

          const first =
            u.first_name
              ? u.first_name.trim()
              : '';

          const last =
            u.last_name
              ? u.last_name.trim()
              : '';

          const fullName =
            `${first} ${last}`.trim();


          return {

            id:
              u.id ||
              u._id,

            name:
              fullName.length > 0
                ? fullName
                : (
                    u.name ||
                    u.email ||
                    'Utilisateur sans nom'
                  )
          };

        });


      setSoignants(filtered);

    } catch (err) {

      console.error(
        'Erreur chargement soignants :',
        err
      );

      toast.error(
        "Impossible de charger les soignants"
      );

    }
  };


  /*
  ==========================================================
  CHARGEMENT DES ÉTABLISSEMENTS
  ==========================================================
  */

  const fetchEtablissements = async () => {

    try {

      const res =
        await axiosInstance.get(
          '/institutions'
        );


      if (!Array.isArray(res.data)) {

        throw new Error(
          "Format inattendu"
        );

      }


      setEtablissementsComplets(
        res.data
      );


      const etablissementsFiltres =
        res.data.map(e => ({

          id:
            e.id ||
            e._id,

          name:
            e.name ||
            '—',

          address:
            e.address ||
            ''

        }));


      setEtablissements(
        etablissementsFiltres
      );


    } catch (err) {

      console.error(
        'Erreur chargement établissements :',
        err
      );

      toast.error(
        "Impossible de charger les établissements"
      );

    }
  };


  /*
  ==========================================================
  CHARGEMENT DES UTILISATEURS POUR LES FILTRES
  ==========================================================

  Accessible aux ADMIN + CHEFS.
  */

  const fetchUsersByRole = async () => {

    try {

      const res =
        await axiosInstance.get(
          '/users'
        );


      const groupedUsers = {};


      res.data.forEach(u => {

        if (
          u.status === 'approved' &&
          u.role !== 'admin'
        ) {

          if (!groupedUsers[u.role]) {

            groupedUsers[u.role] = [];

          }


          const fullName =
            `${u.first_name || ''} ${u.last_name || ''}`
              .trim() ||
            u.email;


          groupedUsers[u.role].push({
            id:
              u.id ||
              u._id,

            uuid:
              u.id ||
              null,

            mongo_id:
              u._id ||
              null,

            name:
              fullName

          });

        }

      });


      setUsersByRole(
        groupedUsers
      );


    } catch (err) {

      console.error(
        'Erreur chargement users :',
        err
      );

    }
  };


  /*
  ==========================================================
  CHANGEMENT D'ÉTABLISSEMENT
  ==========================================================
  */

  const onChangeEtablissement = (
    etabId
  ) => {


    setNewPrestation(prev => ({

      ...prev,

      etablissement_id:
        etabId

    }));


    const etab =
      etablissementsComplets.find(

        e =>
          String(e.id || e._id) ===
          String(etabId)

      );


    if (
      etab &&
      etab.type_tarification ===
        'pourcentage'
    ) {

      setIsTarificationPourcentage(
        true
      );


      setNewPrestation(prev => ({

        ...prev,

        pricing_type:
          'pourcentage'

      }));


    } else {


      setIsTarificationPourcentage(
        false
      );


      setNewPrestation(prev => ({

        ...prev,

        pricing_type:
          'normal'

      }));

    }


    setTypeSoignant('');

    setSoignants([]);

  };


  const editFormRef =
    useRef(null);


  /*
  ==========================================================
  MODIFICATION D'UNE PRESTATION
  ==========================================================

  Sécurité FRONT :
  seuls ADMIN + CHEFS.
  */

  const handleEdit = prestation => {

    if (!canManage) {

      toast.error(
        "Vous n'avez pas l'autorisation de modifier cette prestation."
      );

      return;
    }


    setEditingId(
      prestation._id
    );


    setNewPrestation({

      name:
        prestation.name ||
        '',

      description:
        prestation.description ||
        '',

      assigned_to:
        prestation.assigned_to ||
        '',

      etablissement_id:
        prestation.etablissement_id ||
        '',

      dateStart:
        prestation.dateStart

          ? new Date(
              prestation.dateStart
            )
              .toISOString()
              .slice(0, 16)

          : '',

      dateEnd:
        prestation.dateEnd

          ? new Date(
              prestation.dateEnd
            )
              .toISOString()
              .slice(0, 16)

          : '',

      pricing_type:
        prestation.pricing_type ||
        'normal',

      prestation_type:
        prestation.prestation_type ||
        'normal',

      base_rate:
        prestation.base_rate ??
        0,

      travel_fee:
        prestation.travel_fee ??
        0,

      final_rate:
        prestation.final_rate ??
        0,

      observation:
        prestation.observation ||
        ''

    });


    setTypeSoignant(
      prestation.type_soignant ||
      ''
    );


    /*
    Recharge les soignants pour que le chef puisse
    également modifier l'assignation.
    */

    if (
      prestation.type_soignant
    ) {

      fetchSoignants(
        prestation.type_soignant
      );

    }


    setCreationMode(

      prestation.prestation_type ===
        'urgent'

        ? 'urgent'

        : 'normal'

    );


    setCreating(true);


    setTimeout(() => {

      editFormRef.current
        ?.scrollIntoView({

          behavior:
            'smooth',

          block:
            'center'

        });

    }, 100);

  };


  /*
  ==========================================================
  SUPPRESSION / CORBEILLE
  ==========================================================

  ADMIN + CHEF
  */

  const handleDelete = async id => {

    if (!canManage) {

      toast.error(
        "Vous n'avez pas l'autorisation de supprimer cette prestation."
      );

      return;
    }


    if (
      !confirm(
        'Déplacer vers corbeille ? (Suppression irréversible possible depuis admin)'
      )
    ) {

      return;

    }


    try {

      setPrestations(
        prev =>
          prev.filter(
            p => p._id !== id
          )
      );


      await axiosInstance.delete(
        `/prestations/${id}/trash`
      );


      toast.success(
        "Prestation déplacée vers corbeille !"
      );


    } catch (err) {


      fetchPrestations();


      toast.error(

        err.response?.data?.detail ||

        "Erreur lors du déplacement"

      );

    }
  };


  /*
  ==========================================================
  CRÉATION OU MODIFICATION
  ==========================================================

  ADMIN + CHEF
  */

  const getMonthDays = () => {
    const year = Number(multipleYear);
    const month = Number(multipleMonth);
    const daysInMonth = new Date(year, month, 0).getDate();
    const firstDay = new Date(year, month - 1, 1).getDay();
    const mondayIndex = firstDay === 0 ? 6 : firstDay - 1;
    return { daysInMonth, mondayIndex };
  };

  const toggleMultipleDate = (day) => {
    const dateKey = `${multipleYear}-${multipleMonth}-${String(day).padStart(2, '0')}`;
    setMultipleDates(prev => {
      if (prev[dateKey]) {
        const copy = { ...prev };
        delete copy[dateKey];
        return copy;
      }
      return {
        ...prev,
        [dateKey]: { startTime: multipleStartTime, endDate: dateKey, endTime: multipleEndTime }
      };
    });
  };

  const updateMultipleDateTime = (dateKey, field, value) => {
    setMultipleDates(prev => ({
      ...prev,
      [dateKey]: { ...prev[dateKey], [field]: value }
    }));
  };

  const applyTimesToAllMultipleDates = () => {
    setMultipleDates(prev => Object.fromEntries(
      Object.entries(prev).map(([dateKey]) => [
        dateKey,
        { ...prev[dateKey], startTime: multipleStartTime, endTime: multipleEndTime }
      ])
    ));
  };

  const handleCreateMultiple = async (e) => {
    e.preventDefault();

    if (!canManage) {
      toast.error("Vous n'avez pas l'autorisation de créer des prestations.");
      return;
    }

    if (!newPrestation.etablissement_id) {
      toast.error("Sélectionnez un établissement");
      return;
    }

    if (!typeSoignant) {
      toast.error("Sélectionnez le type de soignant");
      return;
    }

    if (!newPrestation.assigned_to) {
      toast.error("Sélectionnez un soignant");
      return;
    }

    const entries = Object.entries(multipleDates).sort(
      ([dateA], [dateB]) => dateA.localeCompare(dateB)
    );

    if (!entries.length) {
      toast.error("Sélectionnez au moins une date dans le calendrier");
      return;
    }

    setLoading(true);

    try {
      let successCount = 0;
      const errors = [];

      /*
       * IMPORTANT : création volontairement SÉQUENTIELLE.
       *
       * Le backend calcule le tarif de chaque prestation à sa création
       * (tarif établissement, type de soignant, durée, majorations, etc.).
       * Envoyer toutes les requêtes en parallèle pouvait laisser certaines
       * prestations avec base_rate/final_rate à 0.
       *
       * Ici on attend la réponse complète d'une prestation avant de créer
       * la suivante afin que chacune passe par le calcul tarifaire normal.
       */
      for (const [dateKey, times] of entries) {
        try {
          if (!times?.startTime || !times?.endTime) {
            throw new Error(`Horaire incomplet pour le ${dateKey}`);
          }

          const [year, month, day] = dateKey.split('-').map(Number);
          const [sh, sm] = times.startTime.split(':').map(Number);
          const [eh, em] = times.endTime.split(':').map(Number);

          const selectedEndDate = times.endDate || dateKey;
          const [endYear, endMonth, endDay] = selectedEndDate
            .split('-')
            .map(Number);

          const start = new Date(
            year,
            month - 1,
            day,
            sh,
            sm,
            0,
            0
          );

          let end = new Date(
            endYear,
            endMonth - 1,
            endDay,
            eh,
            em,
            0,
            0
          );

          // Si la date de fin reste le même jour mais que l'heure de fin
          // est antérieure ou égale à l'heure de début, il s'agit d'une nuit.
          if (selectedEndDate === dateKey && end <= start) {
            end.setDate(end.getDate() + 1);
          }

          if (end <= start) {
            throw new Error(
              `La date/heure de fin doit être après le début pour le ${dateKey}`
            );
          }

          const durationMinutes = Math.round(
            (end.getTime() - start.getTime()) / 60000
          );

          if (durationMinutes <= 0) {
            throw new Error(`Durée invalide pour le ${dateKey}`);
          }

          const payload = {
            name: newPrestation.name || 'Prestation établissement',
            description: newPrestation.description || typeSoignant,
            assigned_to: newPrestation.assigned_to,
            etablissement_id: newPrestation.etablissement_id,
            dateStart: start.toISOString(),
            dateEnd: end.toISOString(),
            dureeMinutes: durationMinutes,
            type_soignant: typeSoignant,
            pricing_type: newPrestation.pricing_type || 'normal',
            prestation_type: 'normal'
          };


          const response = await axiosInstance.post(
            '/prestations',
            payload
          );


          /*
           * Vérification informative : si le backend renvoie déjà les tarifs,
           * on détecte immédiatement une prestation créée à 0 sans bloquer
           * les autres créations.
           */
          const createdPrestation =
            response.data?.prestation || response.data || {};

          const returnedBase = Number(createdPrestation.base_rate);
          const returnedFinal = Number(createdPrestation.final_rate);

          if (
            returnedBase === 0 &&
            returnedFinal === 0
          ) {
            console.warn(
              `[PRESTATION MULTIPLE] Tarif à 0 pour ${dateKey}. Vérifier le calcul tarifaire backend.`,
              createdPrestation
            );
          }

          successCount += 1;
        } catch (itemError) {
          console.error(
            `[PRESTATION MULTIPLE] Échec ${dateKey}`,
            itemError?.response?.data || itemError
          );

          errors.push({
            date: dateKey,
            message:
              itemError?.response?.data?.detail ||
              itemError?.message ||
              'Erreur inconnue'
          });
        }
      }

      if (successCount === entries.length) {
        toast.success(`${successCount} prestation(s) créée(s) !`);
      } else if (successCount > 0) {
        toast.warning(
          `${successCount} prestation(s) créée(s), ${errors.length} en échec`
        );
      } else {
        throw new Error(
          errors[0]?.message ||
          'Aucune prestation multiple n’a pu être créée'
        );
      }

      setMultipleDates({});
      setMultipleMode(false);
      setCreating(false);

      setNewPrestation(prev => ({
        ...prev,
        name: '',
        description: '',
        assigned_to: '',
        etablissement_id: ''
      }));

      setTypeSoignant('');
      setSoignants([]);

      await fetchPrestations();
    } catch (err) {
      console.error(
        'Erreur création prestations multiples :',
        err?.response?.data || err
      );

      toast.error(
        err?.response?.data?.detail ||
        err?.message ||
        'Erreur lors de la création des prestations multiples'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCreateOrUpdate =
    async e => {

      e.preventDefault();


      if (!canManage) {

        toast.error(
          "Vous n'avez pas l'autorisation de créer ou modifier une prestation."
        );

        return;
      }


      if (!typeSoignant) {

        toast.error(
          "Sélectionnez le type de soignant"
        );

        return;

      }


      setLoading(true);


      try {


        const start =
          new Date(
            newPrestation.dateStart
          );


        const end =
          new Date(
            newPrestation.dateEnd
          );


        if (
          isNaN(start.getTime()) ||
          isNaN(end.getTime()) ||
          end <= start
        ) {

          toast.error(
            "Dates invalides ou incohérentes"
          );

          setLoading(false);

          return;

        }


        const durationMinutes =
          Math.round(
            (end - start) /
            60000
          );


        const payload = {

          name:
            newPrestation.name,

          description:
            newPrestation.description ||
            typeSoignant,

          assigned_to:
            newPrestation.assigned_to ||
            null,

          etablissement_id:
            newPrestation.etablissement_id ||
            null,

          dateStart:
            start.toISOString(),

          dateEnd:
            end.toISOString(),

          dureeMinutes:
            durationMinutes,

          type_soignant:
            typeSoignant,

          pricing_type:
            creationMode === 'urgent'
              ? 'manual'
              : newPrestation.pricing_type,

          prestation_type:
            creationMode,

          ...(creationMode === 'urgent'
            ? {
                base_rate:
                  Number(
                    newPrestation.base_rate
                  )
              }
            : {})
        };


        if (
          creationMode ===
            'urgent' &&

          (
            !Number.isFinite(
              Number(
                newPrestation.base_rate
              )
            ) ||

            Number(
              newPrestation.base_rate
            ) <= 0
          )
        ) {

          toast.error(
            "Veuillez saisir un tarif horaire valide pour la prestation urgente"
          );

          setLoading(false);

          return;

        }


        if (editingId) {


          await axiosInstance.put(

            `/prestations/${editingId}`,

            payload

          );


          toast.success(
            "Prestation modifiée !"
          );


        } else {


          await axiosInstance.post(

            '/prestations',

            payload

          );


          toast.success(
            "Prestation créée !"
          );

        }


        setNewPrestation({

          name: '',

          description: '',

          assigned_to: '',

          etablissement_id: '',

          dateStart: '',

          dateEnd: '',

          pricing_type:
            'normal',

          prestation_type:
            'normal',

          base_rate: 0,

          travel_fee: 0,

          final_rate: 0,

          observation: ''

        });


        setTypeSoignant('');

        setSoignants([]);

        setEditingId(null);

        setCreating(false);

        fetchPrestations();


      } catch (err) {


        const msg =

          err.response?.data?.detail ||

          'Erreur lors de la création/modification';


        toast.error(msg);


      } finally {


        setLoading(false);

      }

    };
      /*
  ==========================================================
  ANNULER MODIFICATION / CRÉATION
  ==========================================================
  */

  const handleCancelEdit = () => {

    setNewPrestation({
      name: '',
      description: '',
      assigned_to: '',
      etablissement_id: '',
      dateStart: '',
      dateEnd: '',
      pricing_type: 'normal',
      prestation_type: 'normal',
      base_rate: 0,
      travel_fee: 0,
      final_rate: 0,
      observation: ''
    });

    setTypeSoignant('');
    setSoignants([]);
    setEditingId(null);
    setIsTarificationPourcentage(false);
    setCreationMode('normal');
    setCreating(false);
    setMultipleMode(false);
    setMultipleDates({});
  };


  /*
  ==========================================================
  ACCEPTATION / DÉBUT / FIN
  ==========================================================
  */

  const handleAccept = async (id) => {
    try {
      await axiosInstance.put(`/prestations/${id}/accept`);
      toast.success("Prestation acceptée");
      fetchPrestations();
    } catch (err) {
      toast.error(
        err.response?.data?.detail ||
        "Erreur lors de l'acceptation"
      );
    }
  };


  const handleStart = async (id) => {
    try {
      await axiosInstance.put(`/prestations/${id}/start`);
      toast.success("Début signalé");
      fetchPrestations();
    } catch (err) {
      toast.error(
        err.response?.data?.detail ||
        "Erreur lors du démarrage"
      );
    }
  };


  const handleEnd = async (id) => {
    try {
      await axiosInstance.put(`/prestations/${id}/end`);
      toast.success("Fin signalée");
      fetchPrestations();
    } catch (err) {
      toast.error(
        err.response?.data?.detail ||
        "Erreur lors de la fin"
      );
    }
  };


  /*
  ==========================================================
  FILTRAGE DES PRESTATIONS
  ==========================================================
  */

  const filteredPrestations = prestations
    .filter(p => {

      if (filterYear || filterMonth) {

        if (!p.dateStart) return false;

        const date = new Date(p.dateStart);

        const year = date
          .getFullYear()
          .toString();

        const month = String(
          date.getMonth() + 1
        ).padStart(2, '0');

        if (
          filterYear &&
          year !== filterYear
        ) {
          return false;
        }

        if (
          filterMonth &&
          month !== filterMonth
        ) {
          return false;
        }
      }


      if (
        filterEtablissement &&
        String(p.etablissement_id) !==
          String(filterEtablissement)
      ) {
        return false;
      }

      


      // if (
      //   filterUser &&
      //   String(p.assigned_to) !==
      //     String(filterUser)
      // ) {
      //   return false;
      // }
      
      if (filterUser) {

        const selectedUser =
          Object.values(usersByRole)
            .flat()
            .find(u =>
              [
                u.id,
                u.uuid,
                u.mongo_id
              ]
                .filter(Boolean)
                .map(String)
                .includes(String(filterUser))
            );


        const selectedIds = new Set(
          [
            filterUser,
            selectedUser?.id,
            selectedUser?.uuid,
            selectedUser?.mongo_id
          ]
            .filter(Boolean)
            .map(value =>
              String(value).trim()
            )
        );


        const prestationIds = [
          p.assigned_to,
          p.assigned_uuid,
          p.assigned_mongo_id
        ]
          .filter(Boolean)
          .map(value =>
            String(value).trim()
          );


        const match =
          prestationIds.some(id =>
            selectedIds.has(id)
          );
        
        


        if (!match) {
          return false;
        }
      }


      return true;
    })

    // ==========================================
    // TRI CHRONOLOGIQUE DES PRESTATIONS
    // ==========================================
    .sort((a, b) => {

      // Les prestations sans date vont à la fin
      if (!a.dateStart && !b.dateStart) {
        return 0;
      }

      if (!a.dateStart) {
        return 1;
      }

      if (!b.dateStart) {
        return -1;
      }

      const dateA =
        new Date(a.dateStart).getTime();

      const dateB =
        new Date(b.dateStart).getTime();

      return dateA - dateB;
    });

      /*
      ==========================================================
      PAGINATION DES PRESTATIONS
      ==========================================================
      */

      const totalPages = Math.max(
        1,
        Math.ceil(
          filteredPrestations.length / ITEMS_PER_PAGE
        )
      );


      const paginatedPrestations =
        filteredPrestations.slice(
          (currentPage - 1) * ITEMS_PER_PAGE,
          currentPage * ITEMS_PER_PAGE
        );


      /*
      Quand on change un filtre,
      on revient automatiquement à la page 1.
      */

      useEffect(() => {

        setCurrentPage(1);

      }, [
        filterYear,
        filterMonth,
        filterEtablissement,
        filterRole,
        filterUser
      ]);


  /*
  ==========================================================
  PRESTATIONS TERMINÉES
  ==========================================================
  */

  const completedPrestations =
    filteredPrestations.filter(
      p =>
        p.status === "completed"
    );


  const nursePrestations =
    completedPrestations.filter(
      p =>
        p.description === "nurse"
    );


  const caregiverPrestations =
    completedPrestations.filter(
      p =>
        p.description === "caregiver"
    );


  /*
  ==========================================================
  CALCULS FINANCIERS
  ==========================================================
  */

  const totalBase =
    filteredPrestations.reduce(
      (sum, p) =>
        sum +
        (Number(p.base_rate) || 0),
      0
    );


  const totalTravel =
    filteredPrestations.reduce(
      (sum, p) =>
        sum +
        (Number(p.travel_fee) || 0),
      0
    );


  const getSalary = p => {

    if (
      p.status !== "completed"
    ) {
      return 0;
    }

    if (
      Number(p.gain_final) > 0
    ) {
      return Number(p.gain_final);
    }

    return (
      Number(p.final_rate) || 0
    );
  };


  const nurseSalary =
    nursePrestations.reduce(
      (sum, p) =>
        sum + getSalary(p),
      0
    );


  const caregiverSalary =
    caregiverPrestations.reduce(
      (sum, p) =>
        sum + getSalary(p),
      0
    );


  const nurseTotalSalary =
    filteredPrestations
      .filter(
        p =>
          p.description === "nurse"
      )
      .reduce(
        (sum, p) =>
          sum +
          (Number(p.gain_final) || 0),
        0
      );


  const caregiverTotalSalary =
    filteredPrestations
      .filter(
        p =>
          p.description === "caregiver"
      )
      .reduce(
        (sum, p) =>
          sum +
          (Number(p.gain_final) || 0),
        0
      );


  const nurseCommission =
    nurseSalary * 0.09;


  const caregiverCommission =
    caregiverSalary * 0.07;


  const nurseNet =
    nurseSalary -
    nurseCommission;


  const caregiverNet =
    caregiverSalary -
    caregiverCommission;


  const totalCommission =
    nurseCommission +
    caregiverCommission;


  const totalRevenue =
    completedPrestations.reduce(
      (sum, p) =>
        sum +
        (Number(p.final_rate) || 0),
      0
    );


  /*
  ==========================================================
  STATISTIQUES
  ==========================================================
  */

  const stats = {

    total:
      filteredPrestations.length,

    pending:
      filteredPrestations.filter(
        p =>
          p.status === "pending"
      ).length,

    accepted:
      filteredPrestations.filter(
        p =>
          p.status === "accepted"
      ).length,

    inProgress:
      filteredPrestations.filter(
        p =>
          p.status === "in_progress"
      ).length,

    completed:
      filteredPrestations.filter(
        p =>
          p.status === "completed"
      ).length,

    totalRevenue:
      filteredPrestations.reduce(
        (sum, p) =>
          sum +
          (Number(p.final_rate) || 0),
        0
      ),

    nurseSalary,

    caregiverSalary,

    nurseCommission,

    caregiverCommission,

    totalBase,

    totalTravel,

    totalCommission,

    nurseNet,

    caregiverNet,

    nurseCount:
      nursePrestations.length,

    caregiverCount:
      caregiverPrestations.length,

    nurseAverage:
      nursePrestations.length
        ? nurseSalary /
          nursePrestations.length
        : 0,

    caregiverAverage:
      caregiverPrestations.length
        ? caregiverSalary /
          caregiverPrestations.length
        : 0,

    companyProfit:
      totalCommission,

    margin:
      filteredPrestations.length
        ? (
            totalCommission /
            filteredPrestations.reduce(
              (sum, p) =>
                sum +
                (Number(p.final_rate) || 0),
              0
            )
          ) * 100
        : 0,

    totalWorkedMinutes:
      filteredPrestations.reduce(
        (sum, p) =>
          sum +
          (Number(p.actual_duration) || 0),
        0
      ),

    totalPlannedMinutes:
      filteredPrestations.reduce(
        (sum, p) =>
          sum +
          (Number(p.dureeMinutes) || 0),
        0
      ),

    institutions:
      new Set(
        filteredPrestations
          .map(
            p =>
              p.etablissement_id
          )
          .filter(Boolean)
      ).size,

    caregivers:
      new Set(
        filteredPrestations
          .map(
            p =>
              p.assigned_to
          )
          .filter(Boolean)
      ).size
  };


  /*
  ==========================================================
  PRESTATION URGENTE
  ==========================================================
  */

  const urgentDurationHours = (() => {

    if (
      !newPrestation.dateStart ||
      !newPrestation.dateEnd
    ) {
      return 0;
    }

    const start =
      new Date(
        newPrestation.dateStart
      );

    const end =
      new Date(
        newPrestation.dateEnd
      );

    if (
      isNaN(start.getTime()) ||
      isNaN(end.getTime()) ||
      end <= start
    ) {
      return 0;
    }

    return (
      (end - start) /
      3600000
    );

  })();


  const urgentEstimatedTotal =
    creationMode === 'urgent'
      ? Number(
          newPrestation.base_rate || 0
        ) * urgentDurationHours
      : 0;


  const completionRate =
    stats.total > 0
      ? (
          (
            stats.completed /
            stats.total
          ) * 100
        ).toFixed(1)
      : 0;


  /*
  ==========================================================
  AFFICHAGE
  ==========================================================
  */

  return (

    <Layout title="Gestion des prestations">


      <h2 className="text-2xl font-semibold text-[#234B4B]">
        Stats des prestations
      </h2>

      <br />


      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-8">


        <Card className="p-5 text-center">

          <h2 className="text-3xl font-bold">
            {stats.total}
          </h2>

          <p>
            Prestations
          </p>

        </Card>


        <Card className="p-5 text-center">

          <h2 className="text-3xl font-bold text-green-600">

            {stats.completed}

          </h2>

          <p>
            Terminées
          </p>

        </Card>


      </div>


      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">


        <Card className="p-5">

          <h2 className="text-xl font-bold text-indigo-700">

            👨‍⚕️ Gains Soignants

          </h2>


          <div className="mt-4 space-y-4">


            <div className="border rounded-lg p-3">

              <div className="font-bold text-blue-700">

                👨‍⚕️ Infirmiers

              </div>


              <div>
                Prestations :
                {' '}
                {stats.nurseCount}
              </div>


              <div>
                Salaire brut :
                {' '}
                {stats.nurseSalary.toFixed(2)}
                {' '}
                €
              </div>


              <div>
                Commission sanacare (9%) :
                {' '}
                {stats.nurseCommission.toFixed(2)}
                {' '}
                €
              </div>


              <div className="font-semibold text-green-700">

                Net infirmiers :
                {' '}
                {stats.nurseNet.toFixed(2)}
                {' '}
                €

              </div>

            </div>


            <div className="border rounded-lg p-3">

              <div className="font-bold text-purple-700">

                🩺 Aides-soignants

              </div>


              <div>
                Prestations :
                {' '}
                {stats.caregiverCount}
              </div>


              <div>
                Salaire brut :
                {' '}
                {stats.caregiverSalary.toFixed(2)}
                {' '}
                €
              </div>


              <div>
                Commission sanacare (7%) :
                {' '}
                {stats.caregiverCommission.toFixed(2)}
                {' '}
                €
              </div>


              <div className="font-semibold text-green-700">

                Net aides-soignants :
                {' '}
                {stats.caregiverNet.toFixed(2)}
                {' '}
                €

              </div>

            </div>

          </div>

        </Card>


        <Card className="p-5">

          <h2 className="text-xl font-bold text-emerald-700">

            💰 Chiffre d'affaires

          </h2>


          <div className="mt-4 space-y-2">


            <div className="flex justify-between">

              <span>
                CA
              </span>

              <b>
                {stats.totalRevenue.toFixed(2)}
                {' '}
                €
              </b>

            </div>


            <div className="flex justify-between">

              <span>
                Salaires bruts des soignants:
              </span>

              <span>

                {(
                  stats.nurseSalary +
                  stats.caregiverSalary
                ).toFixed(2)}
                {' '}
                €

              </span>

            </div>


            <div className="font-bold text-[#234B4B]">

              <div className="bg-[#D4EBEB] rounded-lg p-3">

                💰 Commission totale sanacare

                <div className="text-2xl font-bold">

                  {stats.totalCommission.toFixed(2)}
                  {' '}
                  €

                </div>

              </div>

            </div>

          </div>

        </Card>


        <Card className="p-5 text-center">

          <h2 className="text-2xl font-bold">

            {formatDuration(
              stats.totalWorkedMinutes
            )}

          </h2>

          <p>
            Temps travaillé
          </p>

        </Card>


        <Card className="p-5 text-center">

          <h2 className="text-2xl font-bold">

            {stats.institutions}

          </h2>

          <p>
            Établissements
          </p>

        </Card>

      </div>


      <div className="space-y-8">


        {/* ==================================================
            BOUTONS CRÉATION
            ADMIN + CHEF
        ================================================== */}


        {canManage && (

          <div className="flex flex-wrap justify-end gap-3">


            <Button

              onClick={() => {

                handleCancelEdit();

                setCreationMode(
                  'normal'
                );

                setNewPrestation(
                  prev => ({

                    ...prev,

                    prestation_type:
                      'normal',

                    pricing_type:
                      'normal'

                  })
                );

                setCreating(true);

              }}

              className="bg-[#D4EBEB] hover:bg-[#C4DFDF] text-[#234B4B] border border-[#AFCFCF]"

            >

              <PlusCircle
                className="mr-2"
                size={20}
              />

              Nouvelle prestation

            </Button>


            <Button
              onClick={() => {
                handleCancelEdit();
                setMultipleMode(true);
                setCreationMode('multiple');
                setCreating(true);
              }}
              className="bg-[#D4EBEB] hover:bg-[#C4DFDF] text-[#234B4B] border-2 border-[#7FAEAE] font-semibold"
            >
              <Calendar className="mr-2" size={20} />
              Prestations multiples
            </Button>

            <Button

              onClick={() => {

                handleCancelEdit();

                setCreationMode(
                  'urgent'
                );

                setNewPrestation(
                  prev => ({

                    ...prev,

                    prestation_type:
                      'urgent',

                    pricing_type:
                      'manual',

                    base_rate:
                      0,

                    final_rate:
                      0

                  })
                );

                setCreating(true);

              }}

              className="bg-[#D4EBEB] hover:bg-[#C4DFDF] text-[#234B4B] border-2 border-[#7FAEAE] font-semibold"

            >

              <PlusCircle
                className="mr-2"
                size={20}
              />

              Prestation urgente

            </Button>


            {creating && (

              <Button

                variant="outline"

                onClick={
                  handleCancelEdit
                }

                className="border-[#AFCFCF] text-[#234B4B]"

              >

                Fermer

              </Button>

            )}

          </div>

        )}


        {/* ==================================================
            FORMULAIRE CRÉATION / MODIFICATION
        ================================================== */}


        {creating && canManage && (

          <div ref={editFormRef}>


            <Card className="glass-card border-[#D4EBEB] shadow-md animate-fade-in max-h-[80vh] overflow-y-auto">


              <CardHeader>

                <CardTitle className="text-[#234B4B] flex items-center justify-between">


                  <span>

                    {editingId

                      ? `Modifier: ${newPrestation.name}`

                      : (
                          creationMode ===
                          'urgent'

                            ? 'Créer une prestation urgente'

                            : 'Créer une prestation'
                        )
                    }

                  </span>


                  <Button

                    variant="ghost"

                    size="sm"

                    onClick={
                      handleCancelEdit
                    }

                    className="h-8 w-8 p-0"

                  >

                    <X size={16} />

                  </Button>


                </CardTitle>

              </CardHeader>


              <div className="px-6 pb-3 flex justify-end">


                <Button

                  type="submit"

                  form="prestation-form"

                  disabled={loading}

                  className="bg-[#7FAEAE] hover:bg-[#6B9B9B] text-white font-semibold"

                >

                  {loading

                    ? '...'

                    : editingId

                      ? 'Modifier'

                      : 'Créer maintenant'
                  }

                </Button>


              </div>


              <CardContent>

                {multipleMode ? (
                  <form onSubmit={handleCreateMultiple} className="space-y-5">
                    <div>
                      <Label>Nom de la prestation</Label>
                      <Input
                        placeholder="Ex: Renfort infirmier"
                        value={newPrestation.name}
                        onChange={e => setNewPrestation({ ...newPrestation, name: e.target.value })}
                      />
                    </div>

                    <div>
                      <Label>Établissement *</Label>
                      <select className="w-full border rounded-md p-3" value={newPrestation.etablissement_id}
                        onChange={e => onChangeEtablissement(e.target.value)} required>
                        <option value="">— Choisir un établissement —</option>
                        {etablissements.map(e => <option key={e.id} value={e.id}>{e.name} ({e.address})</option>)}
                      </select>
                    </div>

                    <div>
                      <Label>Type soignant *</Label>
                      <select className="w-full border rounded-md p-3" value={typeSoignant}
                        onChange={e => { const type=e.target.value; setTypeSoignant(type); setNewPrestation(prev=>({...prev,description:type,assigned_to:''})); fetchSoignants(type); }} required>
                        <option value="">— Choisir le type —</option>
                        <option value="nurse">Infirmier</option>
                        <option value="caregiver">Aide-soignant</option>
                      </select>
                    </div>

                    <div>
                      <Label>Soignant *</Label>
                      <select className="w-full border rounded-md p-3" value={newPrestation.assigned_to}
                        onChange={e => setNewPrestation({...newPrestation,assigned_to:e.target.value})} required>
                        <option value="">— Choisir un soignant —</option>
                        {soignants.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                      </select>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div><Label>Année</Label><Input type="number" value={multipleYear} onChange={e=>{setMultipleYear(e.target.value);setMultipleDates({});}} /></div>
                      <div><Label>Mois</Label><select className="w-full border rounded-md p-3" value={multipleMonth} onChange={e=>{setMultipleMonth(e.target.value);setMultipleDates({});}}>
                        {['Janvier','Février','Mars','Avril','Mai','Juin','Juillet','Août','Septembre','Octobre','Novembre','Décembre'].map((m,i)=><option key={m} value={String(i+1).padStart(2,'0')}>{m}</option>)}
                      </select></div>
                    </div>

                    <div className="rounded-xl border border-[#D4EBEB] bg-[#F4FAFA] p-4">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                        <div><Label>Heure début</Label><Input type="time" value={multipleStartTime} onChange={e=>setMultipleStartTime(e.target.value)} /></div>
                        <div><Label>Heure fin</Label><Input type="time" value={multipleEndTime} onChange={e=>setMultipleEndTime(e.target.value)} /></div>
                        <div className="md:col-span-2 flex items-end"><Button type="button" onClick={applyTimesToAllMultipleDates} className="w-full bg-[#7FAEAE] hover:bg-[#6B9B9B] text-white">Appliquer l’horaire à toutes les dates sélectionnées</Button></div>
                      </div>

                      <div className="grid grid-cols-7 gap-2 text-center text-xs font-semibold text-[#234B4B] mb-2">
                        {['Lun','Mar','Mer','Jeu','Ven','Sam','Dim'].map(d=><div key={d}>{d}</div>)}
                      </div>
                      <div className="grid grid-cols-7 gap-2">
                        {Array.from({length:getMonthDays().mondayIndex}).map((_,i)=><div key={`empty-${i}`} />)}
                        {Array.from({length:getMonthDays().daysInMonth},(_,i)=>i+1).map(day=>{
                          const key=`${multipleYear}-${multipleMonth}-${String(day).padStart(2,'0')}`;
                          const selected=!!multipleDates[key];
                          return <button type="button" key={key} onClick={()=>toggleMultipleDate(day)} className={`min-h-14 rounded-lg border font-semibold ${selected?'bg-[#7FAEAE] text-white border-[#6B9B9B]':'bg-white text-[#234B4B] border-[#D4EBEB] hover:bg-[#EAF5F5]'}`}>
                            {day}{selected && <div className="text-[10px] font-normal">{multipleDates[key].startTime} → {multipleDates[key].endDate && multipleDates[key].endDate !== key ? `${new Date(multipleDates[key].endDate + 'T12:00:00').toLocaleDateString('fr-BE', { day: '2-digit', month: '2-digit' })} ` : ''}{multipleDates[key].endTime}</div>}
                          </button>
                        })}
                      </div>
                    </div>

                    {Object.keys(multipleDates).length > 0 && <div className="space-y-2">
                      <Label>Horaires par date ({Object.keys(multipleDates).length} jour(s))</Label>
                      {Object.entries(multipleDates).sort().map(([dateKey,times])=><div key={dateKey} className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end border rounded-lg p-3 bg-white">
                        <div>
                          <Label className="text-xs text-gray-500">Date de début</Label>
                          <strong className="block mt-2">{new Date(dateKey+'T12:00:00').toLocaleDateString('fr-BE')}</strong>
                        </div>
                        <div>
                          <Label className="text-xs text-gray-500">Heure de début</Label>
                          <Input type="time" value={times.startTime} onChange={e=>updateMultipleDateTime(dateKey,'startTime',e.target.value)} />
                        </div>
                        <div>
                          <Label className="text-xs text-gray-500">Date de fin</Label>
                          <Input type="date" min={dateKey} value={times.endDate || dateKey} onChange={e=>updateMultipleDateTime(dateKey,'endDate',e.target.value)} />
                        </div>
                        <div>
                          <Label className="text-xs text-gray-500">Heure de fin</Label>
                          <Input type="time" value={times.endTime} onChange={e=>updateMultipleDateTime(dateKey,'endTime',e.target.value)} />
                        </div>
                      </div>)}
                    </div>}

                    <div className="rounded-lg bg-[#D4EBEB] p-3 text-[#234B4B] font-semibold">{Object.keys(multipleDates).length} date(s) sélectionnée(s)</div>
                    <div className="flex justify-end gap-3">
                      <Button type="button" variant="outline" onClick={()=>{setMultipleMode(false);handleCancelEdit();}}>Annuler</Button>
                      <Button type="submit" disabled={loading} className="bg-[#7FAEAE] hover:bg-[#6B9B9B] text-white">{loading?'Création...':'Créer les prestations'}</Button>
                    </div>
                  </form>
                ) : (

                <form

                  id="prestation-form"

                  onSubmit={
                    handleCreateOrUpdate
                  }

                  className="grid grid-cols-1 md:grid-cols-2 gap-4"

                >


                  <div>

                    <Label>
                      Nom *
                    </Label>


                    <Input

                      placeholder="Ex: Garde de nuit"

                      value={
                        newPrestation.name
                      }

                      onChange={e =>
                        setNewPrestation({

                          ...newPrestation,

                          name:
                            e.target.value

                        })
                      }

                      required

                    />

                  </div>


                  <div>

                    <Label>
                      Date début *
                    </Label>


                    <Input

                      type="datetime-local"

                      value={
                        newPrestation.dateStart
                      }

                      onChange={e =>
                        setNewPrestation({

                          ...newPrestation,

                          dateStart:
                            e.target.value

                        })
                      }

                      required

                    />

                  </div>


                  <div>

                    <Label>
                      Date fin *
                    </Label>


                    <Input

                      type="datetime-local"

                      value={
                        newPrestation.dateEnd
                      }

                      onChange={e =>
                        setNewPrestation({

                          ...newPrestation,

                          dateEnd:
                            e.target.value

                        })
                      }

                      required

                    />

                  </div>


                  <div className="md:col-span-2">

                    <Label>
                      Établissement *
                    </Label>


                    <select

                      className="w-full border rounded-md p-3 focus:ring-2 focus:ring-[#7FAEAE]"

                      value={
                        newPrestation.etablissement_id
                      }

                      onChange={e =>
                        onChangeEtablissement(
                          e.target.value
                        )
                      }

                      required

                    >

                      <option value="">

                        — Choisir un établissement —

                      </option>


                      {etablissements.map(
                        e => (

                          <option
                            key={e.id}
                            value={e.id}
                          >

                            {e.name}
                            {' '}
                            ({e.address})

                          </option>

                        )
                      )}

                    </select>

                  </div>


                  <div className="md:col-span-2">

                    <Label>
                      Type soignant *
                    </Label>


                    <select

                      className="w-full border rounded-md p-3 focus:ring-2 focus:ring-[#7FAEAE]"

                      value={
                        typeSoignant
                      }

                      onChange={e => {

                        const type =
                          e.target.value;

                        setTypeSoignant(
                          type
                        );

                        setNewPrestation({

                          ...newPrestation,

                          description:
                            type,

                          assigned_to:
                            ''

                        });

                        fetchSoignants(
                          type
                        );

                      }}

                      required

                    >

                      <option value="">

                        — Choisir le type —

                      </option>

                      <option value="nurse">
                        Infirmier
                      </option>

                      <option value="caregiver">
                        Aide-soignant
                      </option>

                    </select>

                  </div>


                  {/* =========================================
                      ASSIGNATION
                      ADMIN + CHEF
                  ========================================= */}


                  {canManage && (

                    <div className="md:col-span-2">

                      <Label>
                        Assigner à (optionnel)
                      </Label>


                      <select

                        className="w-full border rounded-md p-3 focus:ring-2 focus:ring-[#7FAEAE]"

                        value={
                          newPrestation.assigned_to
                        }

                        onChange={e =>
                          setNewPrestation({

                            ...newPrestation,

                            assigned_to:
                              e.target.value

                          })
                        }

                      >

                        <option value="">

                          — Aucun soignant —

                        </option>


                        {soignants.map(
                          s => (

                            <option
                              key={s.id}
                              value={s.id}
                            >

                              {s.name}

                            </option>

                          )
                        )}

                      </select>

                    </div>

                  )}


                  {/* =========================================
                      TARIFICATION
                  ========================================= */}


                  {creationMode ===
                  'urgent' ? (


                    <div className="md:col-span-2 rounded-xl border-2 border-[#AFCFCF] bg-[#D4EBEB]/40 p-4">


                      <Label className="text-[#234B4B] font-semibold">

                        Tarif horaire de la prestation urgente (€/h) *

                      </Label>


                      <Input

                        type="number"

                        min="0"

                        step="0.01"

                        placeholder="Ex: 55.00"

                        value={
                          newPrestation.base_rate
                        }

                        onChange={e =>
                          setNewPrestation({

                            ...newPrestation,

                            base_rate:
                              e.target.value

                          })
                        }

                        className="mt-2 bg-white border-[#AFCFCF] focus-visible:ring-[#7FAEAE]"

                        required

                      />


                      <div className="mt-3 rounded-lg bg-white border border-[#AFCFCF] p-3">


                        <div className="flex justify-between text-sm">

                          <span>
                            Durée prévue :
                          </span>

                          <strong>

                            {urgentDurationHours > 0

                              ? `${urgentDurationHours.toFixed(2)} h`

                              : '—'
                            }

                          </strong>

                        </div>


                        <div className="flex justify-between text-sm mt-1">

                          <span>
                            Tarif horaire :
                          </span>

                          <strong>

                            {Number(
                              newPrestation.base_rate || 0
                            ).toFixed(2)}
                            {' '}
                            €/h

                          </strong>

                        </div>


                        <div className="flex justify-between text-base mt-2 pt-2 border-t border-[#D4EBEB] text-[#234B4B]">

                          <span className="font-semibold">
                            Total estimé :
                          </span>

                          <strong>

                            {urgentEstimatedTotal.toFixed(2)}
                            {' '}
                            €

                          </strong>

                        </div>

                      </div>


                      <p className="text-xs text-[#3E6666] mt-2">

                        Le total est calculé automatiquement selon la durée de la prestation.

                      </p>

                    </div>


                  ) : (


                    <div className="md:col-span-2">


                      <Label>
                        Tarification
                      </Label>


                      <select

                        className="w-full border rounded-md p-3 focus:ring-2 focus:ring-[#7FAEAE]"

                        value={
                          newPrestation.pricing_type
                        }

                        onChange={e =>
                          setNewPrestation({

                            ...newPrestation,

                            pricing_type:
                              e.target.value

                          })
                        }

                        disabled={
                          isTarificationPourcentage
                        }

                      >

                        <option value="normal">
                          Normal (tarif fixe BD)
                        </option>

                        <option value="pourcentage">
                          Pourcentage (majoration auto)
                        </option>

                      </select>


                      {isTarificationPourcentage && (

                        <p className="text-xs text-blue-600 mt-1 font-medium">

                          🔒 Pourcentage imposé par l'établissement

                        </p>

                      )}

                    </div>

                  )}


                  <div className="md:col-span-2 sticky bottom-0 z-30 flex justify-end gap-3 p-4 mt-4 bg-white/95 backdrop-blur border-t border-[#D4EBEB] shadow-[0_-8px_20px_rgba(0,0,0,0.08)] rounded-b-xl">


                    <Button

                      type="button"

                      variant="outline"

                      onClick={
                        handleCancelEdit
                      }

                      disabled={loading}

                      className="px-6"

                    >

                      Annuler

                    </Button>


                    <Button

                      type="submit"

                      disabled={loading}

                      className="bg-[#7FAEAE] hover:bg-[#6B9B9B] px-8 font-medium"

                    >

                      {loading

                        ? '...'

                        : editingId

                          ? 'Modifier'

                          : creationMode ===
                            'urgent'

                            ? 'Créer la prestation urgente'

                            : 'Créer la prestation'
                      }

                    </Button>


                  </div>


                </form>
                )}


              </CardContent>


            </Card>


          </div>

        )}
                {/* ==================================================
            FILTRES
        ================================================== */}

        <div className="mb-8">

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 p-6 bg-white/70 backdrop-blur-sm rounded-2xl shadow-xl border border-[#D4EBEB]">


            <div className="space-y-2">

              <Label className="text-xs font-semibold text-[#234B4B] uppercase tracking-wide">

                Année

              </Label>

              <select

                value={filterYear}

                onChange={e =>
                  setFilterYear(
                    e.target.value
                  )
                }

                className="w-full p-3 border border-[#AFCFCF] rounded-xl focus:ring-2 focus:ring-[#7FAEAE] focus:border-[#7FAEAE] transition-all shadow-sm hover:shadow-md hover:border-[#99BEBE]"

              >

                <option value="">
                  Toutes
                </option>

                <option value="2026">
                  2026
                </option>

                <option value="2025">
                  2025
                </option>

                <option value="2024">
                  2024
                </option>

              </select>

            </div>


            <div className="space-y-2">

              <Label className="text-xs font-semibold text-[#234B4B] uppercase tracking-wide">

                Mois

              </Label>

              <select

                value={filterMonth}

                onChange={e =>
                  setFilterMonth(
                    e.target.value
                  )
                }

                className="w-full p-3 border border-[#AFCFCF] rounded-xl focus:ring-2 focus:ring-[#7FAEAE] focus:border-[#7FAEAE] transition-all shadow-sm hover:shadow-md hover:border-[#99BEBE]"

              >

                <option value="">
                  Tous
                </option>

                <option value="01">
                  Janvier
                </option>

                <option value="02">
                  Février
                </option>

                <option value="03">
                  Mars
                </option>

                <option value="04">
                  Avril
                </option>

                <option value="05">
                  Mai
                </option>

                <option value="06">
                  Juin
                </option>

                <option value="07">
                  Juillet
                </option>

                <option value="08">
                  Août
                </option>

                <option value="09">
                  Septembre
                </option>

                <option value="10">
                  Octobre
                </option>

                <option value="11">
                  Novembre
                </option>

                <option value="12">
                  Décembre
                </option>

              </select>

            </div>


            <div className="space-y-2">

              <Label className="text-xs font-semibold text-[#234B4B] uppercase tracking-wide">

                Établissement

              </Label>

              <select

                value={
                  filterEtablissement
                }

                onChange={e =>
                  setFilterEtablissement(
                    e.target.value
                  )
                }

                className="w-full p-3 border border-[#AFCFCF] rounded-xl focus:ring-2 focus:ring-[#7FAEAE] focus:border-[#7FAEAE] transition-all shadow-sm hover:shadow-md hover:border-[#99BEBE]"

              >

                <option value="">
                  Toutes
                </option>

                {etablissements.map(
                  e => (

                    <option
                      key={e.id}
                      value={e.id}
                    >

                      {e.name}

                    </option>

                  )
                )}

              </select>

            </div>


            <div className="space-y-2 md:col-span-1 lg:col-span-1">

              <Label className="text-xs font-semibold text-[#234B4B] uppercase tracking-wide">

                Soignant

              </Label>


              <div className="flex gap-2 h-12">


                <select

                  value={filterRole}

                  onChange={e => {

                    setFilterRole(
                      e.target.value
                    );

                    setFilterUser('');

                  }}

                  className="flex-1 p-3 border border-[#AFCFCF] rounded-xl focus:ring-2 focus:ring-[#7FAEAE] focus:border-[#7FAEAE] transition-all shadow-sm hover:shadow-md hover:border-[#99BEBE] text-sm"

                >

                  <option value="">
                    Rôle
                  </option>

                  <option value="nurse">
                    Infirmiers
                  </option>

                  <option value="caregiver">
                    Aides-soignants
                  </option>

                </select>


                <select

                  value={filterUser}

                  onChange={e =>
                    setFilterUser(
                      e.target.value
                    )
                  }

                  disabled={
                    !filterRole
                  }

                  className="flex-1 p-3 border border-[#AFCFCF] rounded-xl focus:ring-2 focus:ring-[#7FAEAE] focus:border-[#7FAEAE] transition-all shadow-sm hover:shadow-md hover:border-[#99BEBE] text-sm disabled:bg-[#F4FAFA] disabled:border-[#D4EBEB] disabled:cursor-not-allowed"

                >

                  <option value="">

                    {filterRole
                      ? 'Soignant'
                      : "Rôle d'abord"
                    }

                  </option>


                  {filterRole &&
                    usersByRole[
                      filterRole
                    ]?.map(
                      u => (

                        <option
                          key={u.id}
                          value={u.id}
                        >

                          {u.name}

                        </option>

                      )
                    )
                  }

                </select>

              </div>

            </div>

          </div>


          <div className="flex justify-center mt-6">

            <Button

              variant="outline"

              size="lg"

              onClick={() => {

                setFilterYear('');

                setFilterMonth('');

                setFilterEtablissement('');

                setFilterRole('');

                setFilterUser('');

              }}

              className="px-8 py-3 border-2 border-[#AFCFCF] hover:border-[#7FAEAE] hover:bg-[#F4FAFA] transition-all shadow-lg font-semibold text-[#234B4B] hover:text-[#234B4B]"

            >

              🔄 Réinitialiser tous les filtres

            </Button>

          </div>

        </div>


        {/* ==================================================
            LISTE DES PRESTATIONS
        ================================================== */}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">


          {initialLoading ? (

            <div className="col-span-full py-16 flex flex-col items-center justify-center">

              <div className="w-10 h-10 border-4 border-[#D4EBEB] border-t-[#7FAEAE] rounded-full animate-spin" />

              <p className="mt-4 text-[#234B4B] font-semibold">

                Chargement des prestations...

              </p>

              <p className="text-sm text-gray-500 mt-1">

                Les prestations sont en cours de chargement.

              </p>

            </div>

          ) : filteredPrestations.length === 0 ? (

            <div className="col-span-full py-14 text-center">

              <Calendar
                size={42}
                className="mx-auto text-[#7FAEAE] mb-3"
              />

              <p className="font-semibold text-[#234B4B]">

                Aucune prestation pour ces filtres

              </p>

              <p className="text-sm text-gray-500 mt-1">

                Modifiez les filtres ou réinitialisez-les.

              </p>

            </div>

          ) : (

            // filteredPrestations.map(
            paginatedPrestations.map(
              p => {


                const status =
                  p.status ||
                  'pending';


                const planned =
                  p.dureeMinutes ||
                  0;


                const actual =
                  p.actual_duration;


                return (

                  <Card

                    key={p._id}

                    className="glass-card border-[#D4EBEB] hover:shadow-lg transition"

                  >


                    <CardHeader className="p-4 bg-gradient-to-r from-[#F4FAFA] to-[#D4EBEB] border-b">


                      <div className="flex justify-between items-start gap-3">


                        <CardTitle className="text-xl font-bold text-[#234B4B] flex-1">


                          {p.name}


                          <span className="ml-2 px-2 py-1 bg-gray-200 text-xs rounded-full">

                            {status}

                          </span>


                          {p.prestation_type ===
                            'urgent' && (

                            <span className="ml-2 px-2 py-1 bg-[#D4EBEB] text-[#234B4B] border border-[#AFCFCF] text-xs rounded-full font-bold">

                              URGENTE

                            </span>

                          )}


                        </CardTitle>


                        {/* ===================================
                            BOUTONS MODIFIER / CORBEILLE
                            ADMIN + CHEF
                        =================================== */}

                        {canManage && (

                          <div className="flex flex-col gap-1">


                            <div className="flex gap-1">


                              <button

                                onClick={() =>
                                  handleEdit(p)
                                }

                                className="p-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg shadow-md transition-all text-xs font-bold"

                                title="Modifier"

                              >

                                ✏️

                              </button>


                              <button

                                onClick={() =>
                                  handleDelete(
                                    p._id
                                  )
                                }

                                className="p-2 bg-red-500 hover:bg-red-600 text-white rounded-lg shadow-md transition-all text-xs font-bold"

                                title="Corbeille"

                              >

                                🗑️

                              </button>


                            </div>


                            <small className="text-xs text-gray-500 text-right">

                              {isAdmin
                                ? 'admin'
                                : isChef
                                  ? 'chef'
                                  : user.role
                              }

                            </small>


                          </div>

                        )}


                      </div>


                    </CardHeader>


                    <CardContent className="space-y-3 p-4 text-sm text-gray-700">


                      <p className="text-gray-600 italic bg-gray-50 px-3 py-2 rounded-lg text-xs">

                        {p.description ||
                          '—'
                        }

                      </p>


                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 p-3 bg-slate-50 rounded-lg">


                        <div className="text-center">

                          <div className="text-xs text-gray-500 uppercase font-medium">

                            Début

                          </div>

                          <div className="font-mono font-semibold text-gray-900">

                            {p.dateStart
                              ? fmt(
                                  p.dateStart
                                )
                              : '—'
                            }

                          </div>

                        </div>


                        <div className="text-center">

                          <div className="text-xs text-gray-500 uppercase font-medium">

                            Fin

                          </div>

                          <div className="font-mono font-semibold text-gray-900">

                            {p.dateEnd
                              ? fmt(
                                  p.dateEnd
                                )
                              : '—'
                            }

                          </div>

                        </div>


                        <div className="text-center border-l border-gray-200 sm:pl-3">

                          <div className="text-xs text-gray-500 uppercase font-medium">

                            Durée

                          </div>

                          <div className="font-bold text-lg text-[#3E6666]">

                            {formatDuration(
                              planned
                            )}

                          </div>

                        </div>

                      </div>


                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 p-3 bg-emerald-50/50 rounded-lg">


                        <div className="flex items-center gap-2">

                          <Building2

                            size={16}

                            className="text-emerald-600 flex-shrink-0"

                          />

                          <span className="font-medium text-gray-900 truncate">

                            {p.etablissement_name !==
                              '—'

                              ? p.etablissement_name

                              : 'Aucun'
                            }

                          </span>

                        </div>


                        <div className="flex items-center gap-2">

                          <UserCircle2

                            size={16}

                            className="text-indigo-600 flex-shrink-0"

                          />

                          <span className="font-medium text-gray-900 truncate">

                            {p.assigned_to

                              ? p.assigned_name

                              : 'Non assignée'
                            }

                          </span>

                        </div>

                      </div>


                      <div className="grid grid-cols-3 gap-2 p-3 bg-[#F4FAFA] rounded-lg border border-[#D4EBEB]">


                        <div className="text-center">

                          <div className="text-xs text-[#3E6666] font-medium">

                            Base

                          </div>

                          <div className="font-mono font-bold text-[#234B4B] text-sm">

                            {Number.isFinite(
                              p.base_rate
                            )

                              ? p.base_rate.toFixed(
                                  2
                                )

                              : '—'
                            }

                          </div>

                        </div>


                        <div className="text-center">

                          <div className="text-xs text-[#3E6666] font-medium">

                            Dépl.

                          </div>

                          <div className="font-mono font-bold text-[#234B4B] text-sm">

                            {Number.isFinite(
                              p.travel_fee
                            )

                              ? p.travel_fee.toFixed(
                                  2
                                )

                              : '—'
                            }

                          </div>

                        </div>


                        <div className="text-center">

                          <div className="text-xs text-[#3E6666] font-medium">

                            Total

                          </div>

                          <div className="font-bold text-lg bg-[#C4DFDF] px-2 py-1 rounded text-[#234B4B]">

                            {Number.isFinite(
                              p.final_rate
                            )

                              ? p.final_rate.toFixed(
                                  2
                                )

                              : '—'
                            }

                          </div>

                        </div>

                      </div>


                      <div className="flex items-center justify-center py-1">

                        <span className="px-3 py-1 bg-[#D4EBEB] text-xs font-medium text-[#234B4B] rounded-full">

                          {p.prestation_type ===
                            'urgent'

                            ? 'Prix manuel'

                            : (
                                p.pricing_type
                                  ?.toLowerCase() ===
                                'pourcentage'

                                  ? 'Pourcentage'

                                  : 'Fixe'
                              )
                          }

                        </span>

                      </div>


                      {(status ===
                        'in_progress' ||
                        status ===
                        'completed') && (

                        <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">


                          <div className="font-semibold text-blue-900 text-xs mb-2 flex items-center gap-1">

                            <Clock size={14} />

                            TEMPS RÉEL

                          </div>


                          <div className="grid grid-cols-3 gap-2 text-xs">


                            <div className="text-center">

                              <div className="text-blue-600 font-medium">

                                Début

                              </div>

                              <div className="font-mono text-blue-900">

                                {p.started_at

                                  ? fmt(
                                      p.started_at
                                    )

                                  : '—'
                                }

                              </div>

                            </div>


                            <div className="text-center">

                              <div className="text-blue-600 font-medium">

                                Fin

                              </div>

                              <div className="font-mono text-blue-900">

                                {p.ended_at

                                  ? fmt(
                                      p.ended_at
                                    )

                                  : '—'
                                }

                              </div>

                            </div>


                            <div className="text-center">

                              <div className="text-blue-600 font-medium">

                                Durée

                              </div>

                              <div className="font-bold text-blue-900">

                                {formatDuration(
                                  actual
                                )}

                              </div>

                            </div>


                          </div>


                        </div>

                      )}


                      {p.observation && (

                        <div className="p-3 bg-purple-50 rounded-lg border-2 border-purple-200">


                          <div className="font-bold text-purple-900 text-sm mb-1">

                            Observation

                          </div>


                          <p className="text-sm text-purple-800 whitespace-pre-line">

                            {p.observation}

                          </p>


                        </div>

                      )}


                      {status ===
                        'completed' &&
                        Number.isFinite(
                          p.gain_final
                        ) && (

                        <div className="p-3 bg-green-50 rounded-lg border-2 border-green-200">


                          <div className="font-bold text-green-900 text-sm mb-1 flex items-center gap-1">

                            <Euro size={14} />

                            GAIN FINAL

                          </div>


                          <div className="text-2xl font-black text-green-900">

                            {p.gain_final.toFixed(
                              2
                            )}
                            €

                          </div>


                          <div className="text-xs text-green-700 mt-1">

                            {p.ended_at

                              ? `le ${fmt(
                                  p.ended_at
                                )}`

                              : ''
                            }

                          </div>


                        </div>

                      )}


                      <div className="pt-3 border-t flex justify-center">


                        <span

                          className={`px-4 py-2 rounded-full text-xs font-bold shadow-md ${
                            status ===
                            'completed'

                              ? 'bg-green-100 text-green-800 border-2 border-green-200'

                              : status ===
                                'in_progress'

                                ? 'bg-blue-100 text-blue-800 border-2 border-blue-200'

                                : status ===
                                  'accepted'

                                  ? 'bg-yellow-100 text-yellow-800 border-2 border-yellow-200'

                                  : status ===
                                    'pending'

                                    ? 'bg-gray-100 text-gray-800 border border-gray-200'

                                    : 'bg-orange-100 text-orange-800 border-2 border-orange-200'
                          }`}

                        >

                          {status.toUpperCase()}

                        </span>


                      </div>


                    </CardContent>


                  </Card>

                );

              }
            )

                    )}

        </div>


        {/* ==================================================
            PAGINATION
        ================================================== */}

        {!initialLoading &&
          filteredPrestations.length > 0 && (

          <div className="mt-8 mb-8 flex flex-col items-center gap-4">

            <div className="text-sm text-gray-500">

              {filteredPrestations.length} prestation(s) trouvée(s)

              {' • '}

              Page {currentPage} sur {totalPages}

            </div>


            <div className="flex items-center gap-3">

              <Button

                variant="outline"

                disabled={
                  currentPage === 1
                }

                onClick={() => {

                  setCurrentPage(
                    prev =>
                      Math.max(
                        1,
                        prev - 1
                      )
                  );

                }}

                className="border-[#AFCFCF] text-[#234B4B]"

              >

                ← Précédent

              </Button>


              <div className="px-5 py-2 rounded-xl bg-[#D4EBEB] text-[#234B4B] font-bold">

                {currentPage} / {totalPages}

              </div>


              <Button

                variant="outline"

                disabled={
                  currentPage >= totalPages
                }

                onClick={() => {

                  setCurrentPage(
                    prev =>
                      Math.min(
                        totalPages,
                        prev + 1
                      )
                  );

                }}

                className="border-[#AFCFCF] text-[#234B4B]"

              >

                Suivant →

              </Button>

            </div>

          </div>

        )}


      </div>

    </Layout>  
  );

}